import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { resetPassword } from '@/api/auth'
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter'
import type { ApiError } from '@/api/client'

/** Set a new password from the reset-link token (?token=…). On success, points the user to sign in. */
export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  // The same page accepts an invite (?invite=1): only the words differ.
  const invite = params.get('invite') === '1'
  const [password, setPassword] = useState('')
  const reset = useMutation({ mutationFn: (p: string) => resetPassword(token, p) })
  const error = reset.error as ApiError | null

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (password) reset.mutate(password)
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-sm">
        <h1 className="text-lg font-semibold text-brand-darkest mb-2">{invite ? 'Welcome to ProfitPipe' : 'Set a new password'}</h1>
        {invite && !reset.isSuccess && (
          <p className="text-sm text-gray-500 mb-4">Choose a password to finish joining your team. You’ll sign in with the email this invite was sent to.</p>
        )}
        {!token ? (
          <p className="text-sm text-danger">This link is missing its token.</p>
        ) : reset.isSuccess ? (
          <>
            <p className="text-sm text-gray-500 mb-6">
              {invite ? 'You’re all set — sign in with your email and the password you just chose.' : 'Your password has been updated. You can sign in now.'}
            </p>
            <Link
              to="/login"
              className="inline-block w-full text-center bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
            >
              Go to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <label className="block text-xs font-medium text-gray-500 mb-1">{invite ? 'Your password' : 'New password'}</label>
            <input
              type="password"
              value={password}
              placeholder="••••••••"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mb-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
            <PasswordStrengthMeter password={password} />

            {error && <p className="text-danger text-xs mt-3 mb-2">{error.message}</p>}

            <button
              type="submit"
              disabled={reset.isPending || !password}
              className="w-full mt-4 bg-brand-primary text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-60"
            >
              {reset.isPending ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
